/**
 * P15 — "Related product shelf" on `product.customer`.
 *
 * The shelf has two data sources, both server-rendered by this same section so
 * every card stays a real Horizon/Shopify card:
 *
 * - **related** (default). Horizon's `product-recommendations` element fetches
 *   the recommendations endpoint and morphs this section in.
 * - **recently viewed**. The customer's history lives in localStorage, so the
 *   section renders empty and hidden, and this file asks Shopify to render the
 *   section again through its search endpoint with `id:` terms. Horizon's
 *   `RecentlyViewed` helper supplies the ids and `section-renderer` performs the
 *   fetch and the hydration morph. Nothing is rendered client-side.
 *
 * In both cases `RyoxzelRail` — the approved rail controller in
 * `assets/ryoxzel-product-shelf.js` — reads its card set once, when it is
 * constructed. The rail attribute therefore only appears once real cards exist,
 * and this file hands the shelf to the shared controller at that moment. Arrow
 * commands, spring glide, drag, inertia, snapping, keyboard handling,
 * progress/counter hooks and lifecycle cleanup all stay in the shared
 * controller; card and rail visuals stay in `assets/ryoxzel-product-shelf.css`.
 *
 * Loaded only by sections/ryoxzel-pdp-related.liquid.
 */

import { RyoxzelRail } from './ryoxzel-product-shelf.js';
import { sectionRenderer, morphSection } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

const SHELF_SELECTOR = '.ryoxzel-pdp-related';
const RAIL_SELECTOR = '[data-ryoxzel-rail]';

/** Live rails, keyed by their root element. @type {WeakMap<Element, RyoxzelRail>} */
const rails = new WeakMap();

/** Shelves already under observation. @type {WeakSet<Element>} */
const watched = new WeakSet();

/** Observer per shelf, released on `shopify:section:unload`. @type {WeakMap<Element, MutationObserver>} */
const observers = new WeakMap();

/** Recently-viewed shelves already asked Shopify to fill. @type {WeakSet<Element>} */
const hydrated = new WeakSet();

/**
 * Binds the shelf's rail once its cards are present. Idempotent: a shelf keeps
 * one rail instance for as long as it lives.
 * @param {Element} shelf
 */
function bind(shelf) {
  const rail = shelf.querySelector(RAIL_SELECTOR);
  if (!rail || rails.has(rail)) return;
  rails.set(rail, new RyoxzelRail(rail));
}

/**
 * Observes a shelf until its rail root appears. Both hydration paths add the
 * rail attribute to an existing element, so the observer also watches
 * attributes, not only inserted nodes.
 * @param {Element} shelf
 */
function watch(shelf) {
  if (watched.has(shelf)) return;
  watched.add(shelf);
  bind(shelf);

  const observer = new MutationObserver(() => bind(shelf));
  observer.observe(shelf, {
    attributes: true,
    attributeFilter: ['data-ryoxzel-rail'],
    childList: true,
    subtree: true,
  });
  observers.set(shelf, observer);
}

/**
 * @param {Element} shelf
 */
function unwatch(shelf) {
  shelf.querySelectorAll(RAIL_SELECTOR).forEach((rail) => {
    rails.get(rail)?.destroy();
    rails.delete(rail);
  });
  observers.get(shelf)?.disconnect();
  observers.delete(shelf);
  watched.delete(shelf);
}

/**
 * Recently-viewed shelves start hidden and empty. The customer's ids come from
 * Horizon's own store, and Shopify renders the products through the search
 * endpoint, so the shelf keeps its real cards. A shelf with no history — or with
 * products that are no longer available — stays hidden.
 * @param {Element} shelf
 */
async function hydrateRecentlyViewed(shelf) {
  if (hydrated.has(shelf)) return;
  hydrated.add(shelf);

  const sectionId = shelf.dataset.sectionId;
  const currentProductId = shelf.dataset.currentProductId;
  const viewedIds = (RecentlyViewed?.getProducts?.() ?? []).filter(
    (id) => id !== currentProductId,
  );
  if (!sectionId || viewedIds.length === 0) return;

  const url = new URL(Theme.routes.search_url, location.origin);
  url.searchParams.set('q', viewedIds.map((id) => `id:${id}`).join(' OR '));
  url.searchParams.set('resources[type]', 'product');

  try {
    const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
    if (!html?.trim()) return;
    await morphSection(sectionId, html, { mode: 'hydration' });
  } catch {
    // Leave the shelf hidden rather than showing an empty band.
    return;
  }

  const rail = shelf.querySelector(RAIL_SELECTOR);
  if (!rail) return;
  shelf.hidden = false;
  bind(shelf);
}

/**
 * @param {ParentNode} root
 * @returns {Element[]}
 */
function findShelves(root) {
  const shelves = [...root.querySelectorAll(SHELF_SELECTOR)];
  if (root instanceof Element && root.matches(SHELF_SELECTOR)) shelves.unshift(root);
  return shelves;
}

/**
 * @param {ParentNode} root
 */
function sync(root) {
  findShelves(root).forEach((shelf) => {
    watch(shelf);
    if (shelf.dataset.source === 'recently_viewed') hydrateRecentlyViewed(shelf);
  });
}

/**
 * @param {ParentNode} root
 */
function release(root) {
  findShelves(root).forEach(unwatch);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => sync(document), { once: true });
} else {
  sync(document);
}

document.addEventListener('shopify:section:load', (event) => sync(event.target));
document.addEventListener('shopify:section:unload', (event) => release(event.target));
